// =============================================================================
// PageNav.jsx — reusable page-number navigation (Prev · 1 2 3 … N · Next)
// =============================================================================
// Windowed pagination: always shows the first + last page and a small window
// around the current one, with "…" for the gaps (so 45 pages render as
// "1 2 3 4 … 45", not 45 buttons). Pages are 0-based to match caller state.
//   page       — current 0-based page index
//   pageCount  — total number of pages
//   onPage(i)  — called with the new 0-based page index
// Renders nothing when there's a single page.
// =============================================================================

import { usePageSize } from "../../hooks/usePageSize";
import { PAGE_SIZE_OPTIONS } from "../../services/pageSize";

// Build the 1-based display list with "…" gap markers.
function pageItems(current1, total) {
  if (total <= 9) return Array.from({ length: total }, (_, i) => i + 1);
  const items = [1];
  const left = Math.max(2, current1 - 2);
  const right = Math.min(total - 1, current1 + 2);
  if (left > 2) items.push("…");
  for (let i = left; i <= right; i++) items.push(i);
  if (right < total - 1) items.push("…");
  items.push(total);
  return items;
}

export function PageNav({ page, pageCount, onPage, showRows = true }) {
  // Hooks must run before any early return.
  const [rows, setRows] = usePageSize();
  if (!pageCount || pageCount <= 1) return null;
  const current1 = page + 1;
  const items = pageItems(current1, pageCount);
  const btn =
    "border border-zephyr-border rounded-[6px] px-[10px] py-[4px] cursor-pointer bg-white text-zephyr-text disabled:opacity-40 disabled:cursor-not-allowed";
  return (
    <div className="flex gap-[6px] items-center justify-center mt-4 flex-wrap">
      <button
        type="button"
        onClick={() => onPage(Math.max(0, page - 1))}
        disabled={page === 0}
        className={btn}
      >
        Prev
      </button>
      {items.map(function (it, i) {
        if (it === "…") {
          // Stable key from the preceding page number (there are at most two gaps).
          return (
            <span
              key={`gap-${items[i - 1] ?? "start"}`}
              className="px-1 text-zephyr-muted select-none"
            >
              …
            </span>
          );
        }
        const active = it === current1;
        return (
          <button
            key={it}
            type="button"
            onClick={() => onPage(it - 1)}
            className={`border rounded-[6px] px-[11px] py-[4px] cursor-pointer text-[13px] ${
              active
                ? "bg-zephyr-teal-dark border-zephyr-teal-dark text-white font-bold"
                : "bg-white border-zephyr-border text-zephyr-text"
            }`}
          >
            {it}
          </button>
        );
      })}
      <button
        type="button"
        onClick={() => onPage(Math.min(pageCount - 1, page + 1))}
        disabled={page >= pageCount - 1}
        className={btn}
      >
        Next
      </button>
      {/* Rows-per-page lives here so every paginated list gets it for free — it's
          ONE shared per-device preference, not a per-screen one. Changing it jumps
          back to page 1, since the current page index means something different at
          a new size. */}
      {showRows && (
        <label className="flex items-center gap-[6px] ml-4 text-[12px] text-zephyr-muted">
          Rows
          <select
            value={rows}
            aria-label="Rows per page"
            onChange={function (e) {
              setRows(e.target.value);
              onPage(0);
            }}
            className="border border-zephyr-border rounded-[6px] px-[6px] py-[3px] bg-white text-zephyr-text text-[12px]"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
}
